// The agreement screen: on first sign-in and when the legal documents change (and the owner asked everyone to agree
// again), before anything else and after the two-factor and password screens. One required tick for the Terms of service
// and Privacy policy; the main admin also accepts the Data processing agreement for the organisation. In the person's
// screen language (English, Arabic, Chinese); the documents themselves are English, with a note in their language.
import { esc, post } from "../lib.js";
import { navigate, onForm, refreshSession, route, state } from "../state.js";
import { currentLang, t } from "../i18n.js";
import { authCard } from "./auth.js";

const docLink = (id: string, key: string) => {
  const lang = currentLang();
  return `<a href="/legal/${id}${lang === "en" ? "" : `?lang=${lang}`}" target="_blank" rel="noopener">${esc(t(key))}</a>`;
};

route("/agree", () => {
  const need = state.session.legal;
  if (!need) { void navigate("/", true); return; }
  const terms = esc(t("legal.agreeTerms", { terms: "\u0001", privacy: "\u0002" })).replace("\u0001", docLink("terms", "legal.terms")).replace("\u0002", docLink("privacy", "legal.privacy"));
  const dpa = esc(t("legal.agreeDpa", { dpa: "\u0001", org: "\u0002" })).replace("\u0001", docLink("dpa", "legal.dpa")).replace("\u0002", `<strong>${esc(need.orgName)}</strong>`);
  authCard(t("legal.title"), esc(need.again ? t("legal.updated", { version: need.version }) : t("legal.intro", { org: need.orgName })), `
<form data-form="legal-agree" class="falcon-auth-form legal-agree" novalidate>
  <div class="form-message" role="alert" hidden></div>
  <p class="legal-agree-version">${esc(t("legal.version", { version: need.version }))} · ${docLink("terms", "legal.terms")} · ${docLink("privacy", "legal.privacy")} · ${docLink("dpa", "legal.dpa")} · ${docLink("subprocessors", "legal.subprocessors")}</p>
  ${need.terms ? `<label class="legal-agree-tick" data-tick="terms"><input type="checkbox" name="terms"> <span>${terms}</span></label>` : ""}
  ${need.dpa ? `<p class="falcon-auth-sub">${esc(t("legal.dpaNote"))}</p><label class="legal-agree-tick" data-tick="dpa"><input type="checkbox" name="dpa"> <span>${dpa}</span></label>` : ""}
  <p class="legal-agree-note">${esc(t("legal.englishDocs"))}</p>
  <button type="submit" class="primary falcon-auth-submit">${esc(t("legal.continue"))}</button>
  <button type="button" class="link-btn falcon-auth-link" data-action="sign-out">${esc(t("auth.cancelSignOut"))}</button>
</form>`, true, true);
});

onForm("legal-agree", async (form) => {
  const need = state.session.legal;
  const msg = form.querySelector<HTMLElement>(".form-message")!;
  const termsOn = !!form.querySelector<HTMLInputElement>('[name="terms"]')?.checked;
  const dpaOn = !!form.querySelector<HTMLInputElement>('[name="dpa"]')?.checked;
  form.querySelectorAll(".legal-agree-tick").forEach((el) => el.classList.remove("is-missing"));
  const missingTerms = !!need?.terms && !termsOn, missingDpa = !!need?.dpa && !dpaOn;
  if (missingTerms || missingDpa) {
    if (missingTerms) form.querySelector('[data-tick="terms"]')?.classList.add("is-missing");
    if (missingDpa) form.querySelector('[data-tick="dpa"]')?.classList.add("is-missing");
    msg.textContent = t(missingTerms ? "legal.tickTerms" : "legal.tickDpa");
    msg.hidden = false;
    return;
  }
  const btn = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
  btn.disabled = true;
  try {
    await post("/api/legal/agree", { terms: termsOn, dpa: dpaOn });
    await refreshSession();
    await navigate("/app", true);
  } catch (err) {
    msg.textContent = (err as Error).message;
    msg.hidden = false;
  } finally {
    if (btn.isConnected) btn.disabled = false;
  }
});
